import { useCallback, useMemo } from "react"
import { useIntl } from "react-intl"
import { useNavigate } from "react-router-dom"

import { AssignedConversation } from "@doktor-se/bones-ui/dist/web-shared/types"
import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { clearRejectedReassignment } from "api"

import { BooleanCell, DateTimePillCell, NameCell } from "components/v2/conversationList/Cells"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

import { useAppDispatch } from "../../../../lib/hooks"
import { setConversation } from "../../../../reducers/selected"

interface MyErrandsListProps {
  title: string
  conversations: AssignedConversation[]
}

export interface MyErrandsListItem extends ConversationListItem<AssignedConversation> {}

const MyErrandsList = ({ title, conversations }: MyErrandsListProps) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.errand_started" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "paused",
      name: intl.formatMessage({ id: "label.overview.paused" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]
  const shouldNotify = (c: AssignedConversation) => {
    return (c.lastMessage?.id && c.lastMessage.id > (c.lastSeenMessageId ?? 0)) || c.callState === "incoming"
  }

  const mapConversationListItem = useCallback((conv: AssignedConversation): MyErrandsListItem => {
    const isChildConversation = conv.profile?.type === "child"
    return {
      conversation: conv,
      values: {
        pnr: isChildConversation
          ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
          : formatPnr(conv.account?.pnr, conv.metadata?.region),
        name: (
          <NameCell
            name={isChildConversation ? conv.profile!.name! : conv.account?.displayName!}
            isChild={isChildConversation}
          />
        ),
        start: <DateTimePillCell value={conv.created} />,
        listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
        paused: <BooleanCell value={!!conv?.snoozedUntil} />,
        recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
        medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
      },
      sortValues: {
        ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent"], {}),
        start: sortValueFunctions.created(conv),
        paused: sortValueFunctions.paused(conv)
      }
    }
  }, [])
  const list = useMemo(
    () => conversations.map(conv => mapConversationListItem(conv)),
    [conversations, mapConversationListItem]
  )

  const onConversationClick = (item: MyErrandsListItem) => {
    const { id, isReassignmentRejected } = item.conversation
    if (isReassignmentRejected) {
      dispatch(clearRejectedReassignment(id))
    }
    dispatch(setConversation(id))
    navigate("/")
  }

  const shouldNotifyItem = (item: MyErrandsListItem) => {
    return item.conversation.isReassignmentRejected || shouldNotify(item.conversation)
  }

  return (
    <ConversationTable
      title={title}
      columns={columns}
      items={list}
      emptyMessage={intl.formatMessage({ id: "label.overview.my_errands_empty" })}
      onItemClick={onConversationClick}
      shouldNotify={shouldNotifyItem}
    />
  )
}

export default MyErrandsList
